import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ChordTree } from "@/components/mt/ChordTree";
import { NextUp } from "@/components/Callouts";

export default function EarTraining7() {
  const { lesson, course, accent } = findLesson("ear-training", "7");
  return (
    <LessonShell
      course={course}
      courseSlug="ear-training"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/ear-training/ET7-I-IV-V-Animated.mp4"
        caption="Listening for I · IV · V in an 8-bar folk progression"
        hint="≈ 26 sec · watch first"
      />
      <Chapter number="07" eyebrow="Ear Training · Chapter Seven" title="Chord recognition — start with the big three (I, IV, V)">
        <Prose>
          <p>
            Single notes are easy. Chords are harder — they're three or four pitches
            playing simultaneously, blending into a single colour. But you don't have to
            identify every chord cold. Most pop, rock, blues and folk music uses only
            <strong> three primary chords</strong>: I, IV, and V. If you can train your ear
            to distinguish those three, you can transcribe most of the radio.
          </p>
          <p>
            Each of the three has a distinct emotional fingerprint — same kind you learned
            for intervals, but built up to chord-level. The fingerprint is what you train.
          </p>
        </Prose>

        <NoteImage
          src="/notes/ear-training/finals/07a-chords-of-G-major-functional-roles.png"
          caption="Jason's note: the diatonic chords of G major (G, Am, Bm, C, D, Em, F♯°), classified by mood — Major (smiling face: I, IV, V — bright), Minor (sad face: ii, iii, vi — pensive), Diminished (eyes-rolled face: vii° — unstable). The three primaries (G, C, D = I, IV, V) handle most of the song."
        />
      </Chapter>

      <Chapter eyebrow="The three fingerprints" title="What I, IV and V actually feel like">
        <Glossary>
          <Term name="I (the tonic)" def="Home. Stable. Resolved. The chord every phrase wants to end on. Listen for the 'we're done' feeling." />
          <Term name="V (the dominant)" def="Pulling. Tense. Begging to resolve to I. Listen for the 'we need to go home' feeling." />
          <Term name="IV (the subdominant)" def="A journey out — neither home nor pulling-home. Listen for the 'we're on an adventure' feeling. Often paired with I in folk and gospel ('Amen' cadence)." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="The three-chord test" title="Most pop is built from these alone">
        <Prose>
          <p>
            Famous progressions:
          </p>
          <ul>
            <li><strong>I → IV → V → I</strong> — the standard country / folk / blues turnaround</li>
            <li><strong>I → V → vi → IV</strong> — "the four chords of pop" (Beatles, Coldplay, Adele, and many chart hits)</li>
            <li><strong>I → IV → I → V → I</strong> — the gospel / hymn cadence</li>
            <li><strong>I → ♭VII → IV → I</strong> — the Mixolydian rock progression (Hendrix, AC/DC)</li>
          </ul>
          <p>
            A great many pop songs use one of these as their skeleton. Once you can hear the
            switch from I to V to IV, you can transcribe an entire chord chart.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Practice" title="The 'sing the bass' drill">
        <Prose>
          <p>
            The fastest way to identify a chord by ear is to <strong>sing its bass note</strong>.
            That gives you the root — and in most pop and folk the bass note is the root. So
            when a new chord lands in a song, immediately hum its bass note and find it on the
            piano. The interval from the tonic to that bass note tells you which numbered chord
            it is (1, 4, 5, etc.).
          </p>
        </Prose>

        <ChordTree tonic="C" mode="major" />

        <Prose>
          <p>
            Click chords in the tree to hear them. Notice how each one has a distinct
            emotional fingerprint, not just a different sound. With practice you'll start
            to recognise the chord by feel alone, before your conscious mind names it.
          </p>
        </Prose>
      </Chapter>

      <NextUp
        to="/lesson/ear-training-8"
        label="Chapter 8 · Counting Beats & Sub Divisions in Songs"
        why="We return to rhythm — but now applied to actual recordings. We'll listen, find the pulse, identify the time signature, and count out the subdivisions in real time."
      />
    </LessonShell>
  );
}
